import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axios } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const money = (value) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(value) || 0);

const date = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('tr-TR');
};

const rowId = (row) => row?.taksit_id ?? row?.id ?? row?._id;
const studentName = (row) => row?.ogrenci_ad_soyad || row?.ogrenci_adi_soyadi ||
  [row?.ogrenci_adi || row?.ad, row?.ogrenci_soyadi || row?.soyad].filter(Boolean).join(' ') || '-';
const parentName = (row) => row?.veli_ad_soyad || row?.veli_adi_soyadi ||
  [row?.veli_adi || row?.veli_ad, row?.veli_soyadi].filter(Boolean).join(' ') || '-';
const parentPhone = (row) => row?.veli_telefon || row?.veli_telefonu || row?.telefon || '-';
const dueDate = (row) => row?.vade_tarihi || row?.vade || row?.due_date;
const delayDays = (row) => row?.gecikme_gunu ?? row?.gecikme_gun ?? row?.days_overdue ?? 0;
const remainingAmount = (row) => row?.kalan_tutar ?? row?.kalan ?? row?.tutar ?? row?.remaining_amount ?? 0;
const className = (row) => row?.sinif_adi || row?.sinif || row?.sinif_kodu || '-';
const installmentNumber = (row) => row?.taksit_no ?? row?.taksit_sirasi ?? row?.sira_no ?? row?.installment_number;

const responseStats = (payload) => payload?.data?.ozet || payload?.data?.result || payload?.data || payload?.ozet || payload?.result || payload;

function SendResult({ result }) {
  const errors = Array.isArray(result?.errors) ? result.errors : [];
  return (
    <div role="status" className="card" style={{ marginTop: 16, borderColor: 'var(--accent-border)' }}>
      <div style={{ fontWeight: 800, marginBottom: 8 }}>Gönderim sonucu</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', fontSize: '0.85rem' }}>
        <span>Kuyruğa alınan: <strong>{result?.queued ?? 0}</strong></span>
        <span>Atlanan: <strong>{result?.skipped ?? 0}</strong></span>
        <span>Başarısız: <strong>{result?.failed ?? 0}</strong></span>
      </div>
      {errors.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 20, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          {errors.map((error, index) => <li key={index}>{typeof error === 'string' ? error : error?.message || JSON.stringify(error)}</li>)}
        </ul>
      )}
    </div>
  );
}

function InstallmentTable({ installments, selectedIds, onToggle, onToggleAll, loading, activeTab }) {
  const ids = installments.map(rowId).filter((id) => id !== undefined && id !== null);
  const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(String(id)));
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', borderRadius: 22 }}>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table className="table" style={{ minWidth: 950, margin: 0 }}>
          <thead>
            <tr>
              <th style={{ width: 56 }}><input type="checkbox" aria-label="Bu listedeki tüm taksitleri seç" checked={allSelected} disabled={loading || ids.length === 0} onChange={() => onToggleAll(ids)} /></th>
              <th>ÖĞRENCİ / VELİ</th><th>SINIF</th><th>VADE</th><th>GECİKME</th><th>KALAN TUTAR</th><th>TELEFON</th>
            </tr>
          </thead>
          <tbody>
            {installments.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 28, color: 'var(--text-muted)' }}>Bu bölümde taksit bulunmuyor.</td></tr>
            ) : installments.map((row, index) => {
              const id = rowId(row);
              const key = id ?? `${activeTab}-${index}`;
              const days = Number(delayDays(row)) || 0;
              return (
                <tr key={key}>
                  <td><input type="checkbox" aria-label={`${studentName(row)} taksidini seç`} checked={id != null && selectedIds.has(String(id))} disabled={id == null || loading} onChange={() => onToggle(id)} /></td>
                  <td>
                    <div style={{ fontWeight: 800, color: 'var(--text-primary)', textTransform: 'uppercase' }}>{studentName(row)}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.84rem', marginTop: 4, textTransform: 'uppercase' }}>
                      {parentName(row)}{installmentNumber(row) != null ? ` · ${installmentNumber(row)}. taksit` : ''}
                    </div>
                  </td>
                  <td>{className(row)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{date(dueDate(row))}</td>
                  <td style={{ color: activeTab === 'due_today' ? '#d97706' : '#dc2626', fontWeight: 800, whiteSpace: 'nowrap' }}>{activeTab === 'due_today' ? 'Bugün' : `${days} gün`}</td>
                  <td style={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{money(remainingAmount(row))}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{parentPhone(row)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function OdemeHatirlatmalariPage() {
  const [dueToday, setDueToday] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('due_today');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/muhasebe/odeme-hatirlatmalari');
      const payload = data?.data || data || {};
      const todayItems = Array.isArray(payload.due_today) ? payload.due_today : [];
      const overdueItems = Array.isArray(payload.overdue) ? payload.overdue : [];
      setDueToday(todayItems);
      setOverdue(overdueItems);
      const validIds = new Set([...todayItems, ...overdueItems].map(rowId).filter((id) => id != null).map(String));
      setSelectedIds((current) => new Set([...current].filter((id) => validIds.has(id))));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ödeme hatırlatmaları yüklenemedi.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allIds = useMemo(() => [...dueToday, ...overdue].map(rowId).filter((id) => id != null).map(String), [dueToday, overdue]);
  const visibleRows = activeTab === 'due_today' ? dueToday : overdue;
  const dueTodayTotal = useMemo(() => dueToday.reduce((sum, row) => sum + (Number(remainingAmount(row)) || 0), 0), [dueToday]);
  const overdueTotal = useMemo(() => overdue.reduce((sum, row) => sum + (Number(remainingAmount(row)) || 0), 0), [overdue]);
  const toggle = (id) => setSelectedIds((current) => {
    const next = new Set(current);
    if (next.has(String(id))) next.delete(String(id));
    else next.add(String(id));
    return next;
  });
  const toggleAll = (ids) => setSelectedIds((current) => {
    const next = new Set(current);
    const keys = ids.map(String);
    if (keys.length && keys.every((id) => next.has(id))) keys.forEach((id) => next.delete(id));
    else keys.forEach((id) => next.add(id));
    return next;
  });

  const sendSelected = async () => {
    if (selectedIds.size === 0 || sending) return;
    setSending(true);
    setResult(null);
    try {
      const { data } = await axios.post('/muhasebe/odeme-hatirlatmalari/gonder', {
        taksit_ids: allIds.filter((id) => selectedIds.has(id)),
      });
      if (data?.success === false) {
        toast.error(data?.message || 'Seçilenlere hatırlatma gönderilemedi.');
        return;
      }
      setResult(responseStats(data));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Seçilenlere hatırlatma gönderilemedi.');
    } finally { setSending(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: 'clamp(1.35rem,3vw,2rem)', margin: 0 }}>💬 Ödeme Hatırlatmaları</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 'clamp(0.95rem,2vw,1.15rem)', margin: '6px 0 0' }}>Ödeme günü gelen ve geciken taksitleri inceleyip hatırlatmayı elle gönderin.</p>
        </div>
        <button className="btn btn-outline" type="button" onClick={load} disabled={loading || sending} style={{ minHeight: 52, padding: '0 24px', fontWeight: 800 }}>{loading ? 'Yükleniyor…' : '↻ Yenile'}</button>
      </div>
      <div style={{ background: 'var(--accent-bg,#eef2ff)', borderRadius: 14, padding: '20px 24px', color: 'var(--text-secondary,#475569)', fontSize: 'clamp(0.9rem,1.6vw,1.05rem)' }}>
        WhatsApp hatırlatmaları otomatik gönderilmez. Mesajlar yalnızca seçip gönder düğmesine bastığınızda kuyruğa alınır.
      </div>
      {loading ? <div className="page-loader" style={{ minHeight: 180 }}><div className="spinner" /></div> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 20 }}>
            <div className="stat-card" style={{ '--stat-color': '#2563eb', padding: '30px 36px', borderRadius: 22, minHeight: 112, boxSizing: 'border-box' }}>
              <div style={{ fontSize: '1.65rem', color: '#f97316', fontWeight: 500 }}>{dueToday.length}</div>
              <div style={{ color: 'var(--text-muted)', fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase' }}>BUGÜN VADESİ GELEN · {money(dueTodayTotal)}</div>
            </div>
            <div className="stat-card" style={{ '--stat-color': '#2563eb', padding: '30px 36px', borderRadius: 22, minHeight: 112, boxSizing: 'border-box' }}>
              <div style={{ fontSize: '1.65rem', color: '#ef3340', fontWeight: 500 }}>{overdue.length}</div>
              <div style={{ color: 'var(--text-muted)', fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase' }}>ÖDEMESİ GECİKEN · {money(overdueTotal)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className={`btn ${activeTab === 'due_today' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setActiveTab('due_today')} style={{ minHeight: 52, padding: '0 24px', borderRadius: 14, fontWeight: 800 }}>Bugün vadesi gelen ({dueToday.length})</button>
              <button type="button" className={`btn ${activeTab === 'overdue' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setActiveTab('overdue')} style={{ minHeight: 52, padding: '0 24px', borderRadius: 14, fontWeight: 800 }}>Ödemesi geciken ({overdue.length})</button>
            </div>
            <button className="btn" type="button" onClick={sendSelected} disabled={sending || loading || selectedIds.size === 0} style={{ minHeight: 56, padding: '0 28px', borderRadius: 14, background: '#ef3340', color: '#fff', fontWeight: 800, fontSize: '1rem', boxShadow: '0 8px 18px rgba(239,51,64,0.18)' }}>
              {sending ? 'Gönderiliyor…' : 'Seçilenlere hatırlatma gönder'}
            </button>
          </div>
          <InstallmentTable installments={visibleRows} activeTab={activeTab} selectedIds={selectedIds} onToggle={toggle} onToggleAll={toggleAll} loading={sending} />
        </>
      )}
      {result && <SendResult result={result} />}
    </div>
  );
}
